import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MagnifyingGlass, Clock } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/AppShell";
import { Seo } from "@/components/Seo";
import { WeeklyEmailToggle } from "@/components/WeeklyEmailToggle";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api, errMsg, useFetch } from "@/lib/api";
import { QuizPromo } from "@/components/QuizPromo";

const IMGS = {
  "Historical Legislation & Milestones": "https://images.unsplash.com/photo-1633158829875-e5316a358c6f?crop=entropy&cs=srgb&fm=jpg&q=70&w=800",
  "Demographics & Benchmarks": "https://images.unsplash.com/photo-1543286386-2e659306cd6c?crop=entropy&cs=srgb&fm=jpg&q=70&w=800",
  "Investment Strategy Deep-Dives": "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?crop=entropy&cs=srgb&fm=jpg&q=70&w=800",
  "FERS/CSRS & Pension Coordination": "https://images.unsplash.com/photo-1473186578172-c141e6798cf4?crop=entropy&cs=srgb&fm=jpg&q=70&w=800",
  "Tax Optimization": "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&q=70&w=800",
  "Daily TSP Market Recap": "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=70&w=800",
};
export const catImg = (c) => IMGS[c] || IMGS["Tax Optimization"];

export default function ContentHub() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const q = sp.get("q") || "", cat = sp.get("category") || "", page = Number(sp.get("page") || 1);
  const [term, setTerm] = useState(q);
  useEffect(() => setTerm(q), [q]);
  useEffect(() => {
    if (term === q) return;
    const t = setTimeout(() => update({ q: term }), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);
  const cats = useFetch("/articles/categories");
  const { data, loading, reload } = useFetch(`/articles?q=${encodeURIComponent(q)}&category=${encodeURIComponent(cat)}&page=${page}&limit=12`);
  const update = (patch) => {
    const next = { q, category: cat, page: 1, ...patch };
    setSp(Object.fromEntries(Object.entries(next).filter(([, v]) => v && v !== 1 || (v === 1 && false))));
  };
  const total = cats.data?.reduce((s, c) => s + c.count, 0);
  const genRecap = async () => {
    try {
      const r = await api.post("/posts/generate-recap");
      toast.success(r.data.status === "exists" ? "Today's recap already exists" : "Daily recap published");
      cats.reload();
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <Seo title="Content Hub: TSP, IRA & Federal Retirement Articles" description="Plain-English articles and daily TSP market recaps on SECURE 2.0, TSP balances by age, C vs S vs L Funds, FERS pensions, Roth conversions and more." path="/learn" />
      <PageHeader title="Content Hub" subtitle={`${total || 75} articles on TSP rules, benchmarks, strategy, pensions and taxes, plus a daily TSP market recap.`}
        actions={user?.role === "admin" && <Button variant="outline" className="rounded-full" onClick={genRecap} data-testid="generate-recap-button">Generate today's recap</Button>} />
      <WeeklyEmailToggle />
      <QuizPromo testid="hub-quiz-card" className="mb-6" />
      <form onSubmit={(e) => { e.preventDefault(); update({ q: term }); }} className="relative max-w-xl mb-6 fade-up">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search articles, e.g. Roth conversion, G Fund, Rule of 55" className="pl-10 h-11 rounded-full bg-white/80 dark:bg-slate-900/70" data-testid="hub-search-input" />
      </form>
      <div className="flex flex-wrap gap-2 mb-8" data-testid="hub-category-filters">
        <Button size="sm" variant={!cat ? "default" : "outline"} className="rounded-full" onClick={() => update({ category: "" })} data-testid="hub-cat-all">All ({total || "…"})</Button>
        {cats.data?.map((c) => (
          <Button key={c.name} size="sm" variant={cat === c.name ? "default" : "outline"} className="rounded-full" onClick={() => update({ category: c.name })} data-testid={`hub-cat-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{c.name} ({c.count})</Button>
        ))}
      </div>
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-72" />)}</div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mb-4" data-testid="hub-result-count">{data?.total} article{data?.total === 1 ? "" : "s"}{q ? ` matching "${q}"` : ""}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="hub-article-grid">
            {data?.items.map((a, i) => (
              <Link key={a.slug} to={`/learn/${a.slug}`} className="glass glass-lift overflow-hidden group fade-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`article-card-${a.slug}`}>
                <div className="h-36 overflow-hidden"><img src={catImg(a.category)} alt="" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" /></div>
                <div className="p-5">
                  <p className="text-[11px] uppercase tracking-wider text-cyan-700 dark:text-cyan-400">{a.category}</p>
                  <h3 className="font-display text-lg font-semibold mt-2 leading-snug text-slate-900 dark:text-white">{a.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 line-clamp-3">{a.excerpt}</p>
                  <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1"><Clock size={12} /> {a.read_time} min · {a.published_date}</p>
                </div>
              </Link>
            ))}
          </div>
          {data?.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10" data-testid="hub-pagination">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => update({ page: page - 1 })} data-testid="hub-prev-page">Previous</Button>
              {[...Array(data.pages)].map((_, i) => (
                <Button key={i} size="sm" variant={page === i + 1 ? "default" : "ghost"} onClick={() => update({ page: i + 1 })} data-testid={`hub-page-${i + 1}`}>{i + 1}</Button>
              ))}
              <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => update({ page: page + 1 })} data-testid="hub-next-page">Next</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
